import { IActionContext } from '@msdyn365-commerce/core';
import React from 'react';
import { DeliveryDateRange, getDeliveryDateRange } from './estDeliveryDate';
import { getAttributeValuesAsync, getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { DeliveryOption, ProductDeliveryOptions } from '@msdyn365-commerce/retail-proxy';
import * as Msdyn365 from '@msdyn365-commerce/core';

export enum DeliveryBoxesType {
    PDP = 1,
    Cart
}

export const isLowStock = (
    productId: number | undefined,
    productAvailabilities: any[],
    warehouseIdsForFulfillment: string[],
    isPlantCategory: boolean
): boolean => {
    const inventories = productAvailabilities.filter(pa => pa.ProductAvailableQuantity?.ProductId === productId);

    if (isPlantCategory) {
        return inventories.some(
            pa => warehouseIdsForFulfillment.includes(pa.InventLocationId?.toLowerCase()) && pa.StockLevelCode === 'LOW'
        );
    }

    return inventories.some(pa => pa.StockLevelCode === 'LOW');
};

const getSortedDelOptions = (options: DeliveryOption[]) => {
    const nonCnc = options.filter(o => o.Code !== 'ClickCol');
    const cnc = options.find(o => o.Code === 'ClickCol');
    return cnc ? [...nonCnc, cnc] : nonCnc;
};

const modeOfDelChanged = (
    isHomeDelSelected: boolean,
    isCNCSelected: boolean,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void
) => () => updateModeOfAddToCart(isHomeDelSelected, isCNCSelected);

export const getAddToCartBoxes = (
    type: DeliveryBoxesType,
    productId: number,
    isHomeDelSelected: boolean,
    isCNCSelected: boolean,
    isHomeDelAvailable: boolean,
    isCncAvailable: boolean,
    actionContext: IActionContext,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void,
    isHomeDelLowStock?: boolean,
    isCNCLowStock?: boolean
): Promise<(JSX.Element[] | undefined)[]> => {
    if (type === DeliveryBoxesType.PDP) {
        return getAddToCartBoxesForPDP(
            productId,
            isHomeDelSelected,
            isCNCSelected,
            isHomeDelAvailable,
            isCncAvailable,
            actionContext,
            updateModeOfAddToCart,
            isHomeDelLowStock,
            isCNCLowStock
        );
    }

    return Promise.resolve([]);
};

const getAddToCartBoxesForPDP = async (
    productId: number,
    isHomeDelSelected: boolean,
    isCNCSelected: boolean,
    isHomeDelAvailable: boolean,
    isCncAvailable: boolean,
    actionContext: IActionContext,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void,
    isHomeDelLowStock?: boolean,
    isCNCLowStock?: boolean
): Promise<(JSX.Element[] | undefined)[]> => {
    const [deliveryOptions, productAttributes] = await Promise.all([
        getDeliveryOptionsAsync({ callerContext: actionContext, queryResultSettings: {} }, [productId]),
        getAttributeValuesAsync(
            { callerContext: actionContext, queryResultSettings: {} },
            productId,
            actionContext.requestContext.apiSettings.channelId,
            actionContext.requestContext.apiSettings.catalogId
        )
    ]);

    const vendorName = productAttributes?.find(a => a.Name?.toLowerCase() === 'vendorname')?.TextValue;

    const isISO = productAttributes?.find(attr => attr.Name?.toLowerCase() === 'onlyinstore')?.TextValue?.toLowerCase() === 'yes';

    const isHomeOnly = isHomeDelAvailable && !isCncAvailable;

    return deliveryOptions.map(delOpt =>
        getSortedDelOptions(delOpt.DeliveryOptions || []).map(mode => {
            if (!isHomeDelAvailable && !isCncAvailable) {
                return <></>;
            }

            const code = mode.Code || '';
            const estDeliveryDate = getDeliveryDateRange(new Date(), actionContext, code);
            const isStdOrPf = code === 'STD' || code === 'PF';

            const shouldRenderHomeTile = !isISO && (isHomeDelAvailable || (isStdOrPf && isCncAvailable));

            const shouldRenderCncTile = !isHomeOnly && (isCncAvailable || (isStdOrPf && isHomeDelAvailable));

            if (
                [
                    'STD',
                    'STD-2',
                    'PF',
                    'HD',
                    'HD Presale',
                    'DTC-02days',
                    'DTC-05days',
                    'DTC-10days',
                    'DTC-14days',
                    'DTC-28days',
                    'DTC-40days'
                ].includes(code) &&
                shouldRenderHomeTile
            ) {
                return getJSXForHomeDelBase(
                    mode,
                    isCncAvailable,
                    isHomeDelAvailable,
                    isHomeDelSelected,
                    updateModeOfAddToCart,
                    vendorName,
                    isHomeDelLowStock
                );
            }

            if (code === 'ClickCol' && shouldRenderCncTile) {
                return getJSXForCNCModes(
                    mode,
                    isCNCSelected,
                    isHomeDelSelected,
                    isHomeDelAvailable,
                    isCncAvailable,
                    updateModeOfAddToCart,
                    vendorName,
                    estDeliveryDate,
                    '',
                    isCNCLowStock
                );
            }

            return <></>;
        })
    );
};

const getJSXForCNCModes = (
    cncMode: DeliveryOption,
    isCNCSelected: boolean,
    homeDelSelected: boolean,
    isHomeDelAvailable: boolean,
    isCnCAvailable: boolean,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void,
    vendorName?: string,
    estDeliveryDate?: DeliveryDateRange | null,
    orgUnitName?: string,
    showLowStockWarning?: boolean
) => {
    const text = 'Free Click & Collect';

    return (
        <>
            {!isHomeDelAvailable && <p className='only-cnc'>Product is only available for Click & Collect.</p>}
            <label className={`msc-cart-line__bopis-store-pickup ${!isCnCAvailable ? 'disabled' : ''}`}>
                <input
                    className='delivery-radio'
                    type='radio'
                    name='shippingTypeHomeDel'
                    disabled={!isCnCAvailable}
                    onChange={isCnCAvailable ? modeOfDelChanged(false, true, updateModeOfAddToCart) : undefined}
                    checked={isCNCSelected}
                />
                <span className='msc-cart-line__custom-radio' />
                <div className='msc-cart-line__delivery-option'>
                    <div className='msc-cart-line__delivery-option__header'>
                        <p>{text}</p>
                        <span className='msc-cart-line__delivery-option__header-icon' />
                    </div>
                    <div className='msc-cart-line__delivery-option__details'>
                        {!isCnCAvailable ? (
                            <p>This item is not available for Click & Collect.</p>
                        ) : (
                            <>
                                <p>
                                    Ready within <b>24 hours</b> if you order before <b>4pm</b>.
                                </p>
                                {showLowStockWarning && <p className='delivery-note'>Hurry, just a few left - order soon!</p>}
                            </>
                        )}
                    </div>
                </div>
            </label>
        </>
    );
};

const getJSXForHomeDelBase = (
    mode: DeliveryOption,
    isCnCAvailable: boolean,
    isHomeDelAvailable: boolean,
    homeDelSelected: boolean,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void,
    vendorName?: string,
    showLowStockWarning?: boolean
) => {
    const text = 'Home Delivery';

    return (
        <>
            {!isCnCAvailable && <p className='only-hd'>Product is only available for Home Delivery.</p>}
            <label className={`msc-cart-line__bopis-shipping ${!isHomeDelAvailable ? 'disabled' : ''}`}>
                <input
                    className='delivery-radio'
                    type='radio'
                    name='shippingTypeHomeDel'
                    disabled={!isHomeDelAvailable}
                    onChange={isHomeDelAvailable ? modeOfDelChanged(true, false, updateModeOfAddToCart) : undefined}
                    checked={homeDelSelected}
                />
                <span className='msc-cart-line__custom-radio' />
                <div className='msc-cart-line__delivery-option'>
                    <div className='msc-cart-line__delivery-option__header'>
                        <p>
                            {text} <span>- dispatched by {vendorName || 'Dobbies'}</span>
                        </p>
                        <span className='msc-cart-line__delivery-option__header-icon'></span>
                    </div>
                    <div className='msc-cart-line__delivery-option__details'>
                        {!isHomeDelAvailable ? (
                            <p>This item is not available for Home Delivery.</p>
                        ) : (
                            <>
                                <p>
                                    Delivery: <span>{mode.Description}</span>
                                </p>
                                {showLowStockWarning && <p className='delivery-note'>Hurry, just a few left - order soon!</p>}
                            </>
                        )}
                    </div>
                </div>
            </label>
        </>
    );
};

const DELIVERY_WITH_ETA = new Set([
    'STD',
    'STD-2',
    'HD',
    'HD Presale',
    'PF',
    'DTC-02days',
    'DTC-05days',
    'DTC-10days',
    'DTC-14days',
    'DTC-28days',
    'DTC-40days'
]);

interface IProductArrivalEstimateProps {
    productId: number;
    actionContext: IActionContext;
    deliveryOptionsData?: ProductDeliveryOptions;
    loggedInZipCode?: string;
    alternateAddressBtn: React.ReactElement;
}

export const ProductArrivalEstimate = ({
    actionContext,
    loggedInZipCode,
    productId,
    alternateAddressBtn
}: IProductArrivalEstimateProps) => {
    const [deliveryOptions, setDeliveryOptions] = React.useState<DeliveryOption[] | undefined>([]);

    React.useEffect(() => {
        getDeliveryOptionsAsync({ callerContext: actionContext }, [productId]).then(res => {
            const match = res.find(r => r.ProductId === productId);
            setDeliveryOptions(match?.DeliveryOptions);
        });
    }, [productId]);

    if (!deliveryOptions?.length) {
        return null;
    }

    return (
        <div className='estimated-arrival'>
            {deliveryOptions.map(opt => (
                <DeliveryEta
                    key={opt.Code}
                    actionContext={actionContext}
                    deliveryModeCode={opt.Code}
                    loggedInZipCode={loggedInZipCode}
                    alternateAddressBtn={alternateAddressBtn}
                />
            ))}
        </div>
    );
};

interface IDeliveryETAProps {
    actionContext: IActionContext;
    deliveryModeCode?: string;
    loggedInZipCode?: string;
    alternateAddressBtn: React.ReactElement;
}

const DeliveryEta = ({ deliveryModeCode, actionContext, loggedInZipCode, alternateAddressBtn }: IDeliveryETAProps) => {
    const code = deliveryModeCode || '';
    const config = actionContext.requestContext.app.config.additionalDeliveryMessages;

    const estDeliveryDate = React.useMemo(() => getDeliveryDateRange(new Date(), actionContext, code), [actionContext, code]);

    const msg = config?.find((c: { delMode: string }) => c.delMode === code)?.msg;
    const isLoggedInUser = actionContext.requestContext.user.isAuthenticated;
    const formattedMsg = isLoggedInUser && msg ? msg.replace(/\./, '') : msg;

    if (formattedMsg) {
        return (
            <p className='d-block additional-delivery-message'>
                <Msdyn365.RichTextComponent text={formattedMsg} />

                {loggedInZipCode && (
                    <>
                        {` to `}
                        <span>{loggedInZipCode.toUpperCase()}</span>
                    </>
                )}
                {loggedInZipCode && alternateAddressBtn}
            </p>
        );
    }

    if (!DELIVERY_WITH_ETA.has(code) || !estDeliveryDate?.maxDeliveryDate) {
        return null;
    }

    return (
        <p>
            Estimated arrival by:{' '}
            <span className='msc-cart-line__delivery-date'>
                {estDeliveryDate.maxDeliveryDate}
                {loggedInZipCode && (
                    <>
                        <span>to</span> {loggedInZipCode.toUpperCase()}
                    </>
                )}
                {loggedInZipCode && alternateAddressBtn}
            </span>
        </p>
    );
};

export const getEstimatedArrivalBy = async (productId: number, actionContext: IActionContext, loggedInZipCode?: string) => {
    const deliveryOptions = await getDeliveryOptionsAsync({ callerContext: actionContext, queryResultSettings: {} }, [productId]);

    const additionalMsgConfig = actionContext.requestContext.app.config.additionalDeliveryMessages;

    let msg = '';

    deliveryOptions.forEach(opt =>
        opt.DeliveryOptions?.forEach(mode => {
            const code = mode.Code || '';
            const est = getDeliveryDateRange(new Date(), actionContext, code);
            const custom = additionalMsgConfig?.find((c: { delMode: string }) => c.delMode === code)?.msg;

            if (custom) {
                msg = `<p>${custom}</p>`;
            } else if (DELIVERY_WITH_ETA.has(code)) {
                msg = `<p>Estimated arrival by: ${
                    est?.maxDeliveryDate
                        ? `<span class='msc-cart-line__delivery-date'>${est.maxDeliveryDate}${
                              loggedInZipCode ? ' to ' + loggedInZipCode.toUpperCase() : ''
                          }</span>`
                        : ''
                }</p>`;
            }
        })
    );

    return msg;
};
